// Urlaub beantragen — und sehen, was daraus geworden ist.
//
// Der Mitarbeiter stellt den Antrag, Sebastian entscheidet. Dass sich niemand
// selbst genehmigen kann, ist keine Frage dieser Oberfläche: Die Datenbank lässt
// ein Einfügen mit Status "genehmigt" gar nicht erst zu (Migration 031).

import { useMemo, useState } from "react";
import {
  useMeineAbwesenheiten,
  useUrlaubskonto,
  useUrlaubBeantragen,
  useAntragZurueckziehen,
  arbeitstage,
  artLabel,
  zeitraum,
  ARTEN,
  type Abwesenheit,
  type Abwesenheitsart,
} from "@/lib/urlaub";
import { LogoLoader } from "@/components/LogoLoader";
import { Button, Card, EmptyState, Field, Input, Sheet, cn } from "@/design-system/ui";

export function UrlaubTab() {
  const { data: konto } = useUrlaubskonto();
  const { data: antraege = [], isLoading } = useMeineAbwesenheiten();
  const [neu, setNeu] = useState(false);

  const offen = antraege.filter((a) => a.status === "beantragt");
  const rest = konto?.rest ?? 0;

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {/* Urlaubskonto */}
        <Card className="mb-5 p-4">
          <div className="flex items-baseline justify-between">
            <span className="text-xs uppercase tracking-wider text-ink-400">
              Resturlaub {new Date().getFullYear()}
            </span>
            <span className="text-xs tabular-nums text-ink-500">
              {konto?.anspruch ?? 0} Tage Anspruch
            </span>
          </div>

          <div
            className={cn(
              "mt-1 text-3xl font-semibold tabular-nums",
              rest <= 0 ? "text-warn-fg" : "text-ink-50"
            )}
          >
            {tage(rest)}
          </div>

          <div className="mt-2 flex gap-4 text-sm tabular-nums text-ink-400">
            <span>{tage(konto?.genommen ?? 0)} genommen</span>
            {(konto?.beantragt ?? 0) > 0 && (
              <span className="text-warn-fg">{tage(konto!.beantragt)} beantragt</span>
            )}
          </div>
        </Card>

        {offen.length > 0 && (
          <p className="mb-4 rounded-lg border border-warn-bg/40 bg-warn-bg/10 px-3 py-2 text-sm text-warn-fg">
            {offen.length === 1
              ? "Ein Antrag wartet auf Sebastians Entscheidung."
              : `${offen.length} Anträge warten auf Sebastians Entscheidung.`}
          </p>
        )}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <LogoLoader size="md" label="Anträge werden geladen" />
          </div>
        ) : antraege.length === 0 ? (
          <EmptyState
            title="Noch kein Antrag"
            hint="Urlaub, Krankheit und Freistellung werden hier eingetragen."
            action={
              <Button variant="primary" onClick={() => setNeu(true)}>
                Urlaub beantragen
              </Button>
            }
          />
        ) : (
          <ul className="space-y-2">
            {antraege.map((a) => (
              <li key={a.id}>
                <AntragZeile a={a} />
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="shrink-0 border-t border-ink-700/70 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <Button variant="primary" size="lg" className="w-full" onClick={() => setNeu(true)}>
          Abwesenheit beantragen
        </Button>
      </div>

      {neu && <AntragSheet onClose={() => setNeu(false)} rest={rest} />}
    </>
  );
}

const tage = (n: number) => `${n.toLocaleString("de-DE")} ${n === 1 ? "Tag" : "Tage"}`;

function AntragZeile({ a }: { a: Abwesenheit }) {
  const zurueck = useAntragZurueckziehen();

  const farbe =
    a.status === "genehmigt"
      ? "border-gruen-500/30 bg-gruen-500/5"
      : a.status === "abgelehnt"
        ? "border-danger-bg/30 bg-danger-bg/5"
        : a.status === "zurueckgezogen"
          ? "border-ink-700 opacity-60"
          : "border-warn-bg/40 bg-warn-bg/5";

  return (
    <Card className={cn("p-4", farbe)}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[15px] font-medium text-ink-100">
            {artLabel(a.art)} · {zeitraum(a.von, a.bis, a.halber_tag)}
          </div>
          <div className="mt-0.5 text-sm tabular-nums text-ink-400">
            {tage(Number(a.arbeitstage))}
          </div>
        </div>

        <StatusPille status={a.status} />
      </div>

      {a.begruendung && <p className="mt-2 text-sm text-ink-400">{a.begruendung}</p>}

      {a.entscheidung_kommentar && (
        <p className="mt-2 rounded-lg bg-ink-900 px-3 py-2 text-sm text-ink-300">
          <span className="text-ink-500">Antwort: </span>
          {a.entscheidung_kommentar}
        </p>
      )}

      {/* Zurückziehen geht nur, solange niemand entschieden hat — danach wäre es
          eine nachträgliche Änderung an einer getroffenen Entscheidung. */}
      {a.status === "beantragt" && (
        <Button
          size="sm"
          className="mt-3"
          loading={zurueck.isPending}
          onClick={() => zurueck.mutate(a.id)}
        >
          Antrag zurückziehen
        </Button>
      )}
    </Card>
  );
}

function StatusPille({ status }: { status: Abwesenheit["status"] }) {
  const T: Record<Abwesenheit["status"], { text: string; cls: string }> = {
    beantragt: { text: "wartet", cls: "border-warn-bg/40 bg-warn-bg/10 text-warn-fg" },
    genehmigt: { text: "genehmigt", cls: "border-gruen-500/40 bg-gruen-500/10 text-gruen-400" },
    abgelehnt: { text: "abgelehnt", cls: "border-danger-bg/40 bg-danger-bg/10 text-danger-fg" },
    zurueckgezogen: { text: "zurückgezogen", cls: "border-ink-700 text-ink-500" },
  };
  const t = T[status];
  return (
    <span className={cn("shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-medium", t.cls)}>
      {t.text}
    </span>
  );
}

function AntragSheet({ onClose, rest }: { onClose: () => void; rest: number }) {
  const beantragen = useUrlaubBeantragen();

  const heute = new Date().toISOString().slice(0, 10);
  const [art, setArt] = useState<Abwesenheitsart>("urlaub");
  const [von, setVon] = useState(heute);
  const [bis, setBis] = useState(heute);
  const [halberTag, setHalberTag] = useState(false);
  const [grund, setGrund] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);

  const tageAnzahl = useMemo(() => arbeitstage(von, bis, halberTag), [von, bis, halberTag]);
  const einTag = von === bis;

  const zuViel = art === "urlaub" && tageAnzahl > rest;
  const gueltig = tageAnzahl > 0 && bis >= von;

  return (
    <Sheet
      open
      onClose={onClose}
      title="Abwesenheit beantragen"
      footer={
        <div className="space-y-2">
          {fehler && (
            <p className="rounded-lg border border-danger-bg/30 bg-danger-bg/10 px-3 py-2 text-sm text-danger-fg">
              {fehler}
            </p>
          )}

          {zuViel && (
            <p className="rounded-lg border border-warn-bg/40 bg-warn-bg/10 px-3 py-2 text-sm text-warn-fg">
              Das sind mehr Tage, als du noch hast ({tage(rest)}). Beantragen kannst du es
              trotzdem — Sebastian entscheidet.
            </p>
          )}

          <Button
            variant="primary"
            size="lg"
            className="w-full"
            disabled={!gueltig}
            loading={beantragen.isPending}
            onClick={async () => {
              setFehler(null);
              try {
                await beantragen.mutateAsync({
                  art,
                  von,
                  bis,
                  halber_tag: halberTag && einTag,
                  begruendung: grund.trim() || null,
                });
                onClose();
              } catch (e) {
                setFehler((e as Error).message);
              }
            }}
          >
            {gueltig ? `${tage(tageAnzahl)} beantragen` : "Zeitraum wählen"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <span className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-ink-400">
            Was
          </span>
          <div className="space-y-1.5">
            {ARTEN.map((a) => (
              <button
                key={a.wert}
                onClick={() => setArt(a.wert)}
                className={cn(
                  "flex w-full items-start gap-3 rounded-lg border px-4 py-3 text-left",
                  "transition-colors duration-fast",
                  art === a.wert
                    ? "border-gruen-500/50 bg-gruen-500/10"
                    : "border-ink-700 bg-ink-900 hover:border-ink-600"
                )}
              >
                <span
                  className={cn(
                    "mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border-2",
                    art === a.wert ? "border-gruen-500" : "border-ink-600"
                  )}
                >
                  {art === a.wert && <span className="h-2.5 w-2.5 rounded-full bg-gruen-500" />}
                </span>
                <span className="min-w-0">
                  <span className="block text-[15px] font-medium text-ink-100">{a.label}</span>
                  <span className="mt-0.5 block text-sm text-ink-400">{a.hinweis}</span>
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-3">
          <Field label="Von">
            <Input
              type="date"
              value={von}
              onChange={(e) => {
                setVon(e.target.value);
                if (bis < e.target.value) setBis(e.target.value);
              }}
            />
          </Field>
          <Field label="Bis">
            <Input type="date" value={bis} min={von} onChange={(e) => setBis(e.target.value)} />
          </Field>
        </div>

        {einTag && (
          <button
            onClick={() => setHalberTag((v) => !v)}
            className={cn(
              "flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors duration-fast",
              halberTag ? "border-gruen-500/40 bg-gruen-500/10" : "border-ink-700 bg-ink-900"
            )}
          >
            <span
              className={cn(
                "grid h-5 w-5 shrink-0 place-items-center rounded border-2 text-xs font-bold",
                halberTag ? "border-gruen-500 bg-gruen-500 text-gruen-on" : "border-ink-600"
              )}
            >
              {halberTag && "✓"}
            </span>
            <span className="text-[15px] text-ink-100">Nur ein halber Tag</span>
          </button>
        )}

        <Card className="p-4">
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-ink-400">Arbeitstage</span>
            <span className="text-xl font-semibold tabular-nums text-ink-50">
              {tageAnzahl.toLocaleString("de-DE")}
            </span>
          </div>
          <p className="mt-1 text-xs text-ink-500">
            Samstag und Sonntag zählen nicht. Feiertage prüft Sebastian.
          </p>
        </Card>

        <Field label="Anmerkung" optional>
          <Input
            value={grund}
            onChange={(e) => setGrund(e.target.value)}
            placeholder={art === "krank" ? "z. B. Attest folgt" : "z. B. Familienurlaub"}
          />
        </Field>
      </div>
    </Sheet>
  );
}
